import API from 'files/api.js';

const buildRequest = (url, props) => {
    let newURL = `${url}?type=${props.type}`;
    for(var i in props) {
        if(i == 'type') {
            continue;
        }
        newURL += `&${i}=${typeof(props[i]) === 'object' ? JSON.stringify(props[i]) : encodeURIComponent(props[i])}`;
    }
    return newURL;
}

const download = async (utils, endpoint, props) => {
    return new Promise(async (resolve, reject) => {
        try {

            // prepare url for request
            let url = buildRequest(`${API.server}/api/v${API.version}${endpoint}`, props);

            // build url and xml request
            let xhr = new XMLHttpRequest();
            xhr.open('GET', url);
            xhr.withCredentials = true;

            // set authentication headers for request
            let headers = utils.api.headers();
            Object.keys(headers).filter(key => key !== 'Content-Type').forEach(key => xhr.setRequestHeader(key, headers[key]));

            // prepare form components for request
            let data = new FormData();
            Object.keys(props).forEach(key => {
                if(typeof(props[key]) !== 'object') {
                    return data.append(key, props[key]);
                }
                return data.append(key, JSON.stringify(props[key]));
            });

            // send request to server declare event handlers for response
            xhr.send(data);
            xhr.onreadystatechange = () => {
                if(xhr.readyState == 2) {
                    xhr.responseType = xhr.status == 200 ? 'blob' :'text';
                }
                if(xhr.readyState == 4) {

                    // throw an upstream error if session has expired
                    if(xhr.status === statusCodes.expired_session) {
                        return showSessionExpiredAlert(utils);
                    }

                    // prevent moving forward if any other status than success is returned
                    if(xhr.status !== 200) {
                        let error = new Error(xhr.responseText || 'An unknown error occurred');
                        reject(error);
                        return;
                    }
                    
                    // check for conflicts and allow user to respond
                    if(xhr.status === 409) {
                        let error = new Error();
                        error.code = xhr.status;
                        error.category = xhr.response.category;
                        error.message = xhr.response.message;
                        error.response = xhr.response;
                        reject(error);
                        return;
                    }

                    // prepare link element for automatic download
                    let fileName = xhr.getResponseHeader('Content-Disposition')?.split('attachment;filename=')[1];
                    let link = document.createElement('a');
                    link.href = window.URL.createObjectURL(xhr.response);
                    link.download = fileName;
                    link.click();
                    resolve();
                }
            }

        } catch(e) {
            reject(e);
        }
    });
}

const get = async (utils, url, props) => {
    return new Promise(async (resolve, reject) => {
        try {

            let response = await fetch(buildRequest(`/api/v${API.version}${url}`, props), {
                headers: utils.api.headers(),
                method: 'GET',
                withCredentials: true
            });

            // determine if response code represents a 5xx level error
            if([502, 503, 504].includes(response.status)) {
                throw new Error('Please try again in a few minutes');
            }

            // throw an upstream error if session has expired
            if(response.status === statusCodes.expired_session) {
                return showSessionExpiredAlert(utils);
            }

            // determine if an error with a payload was thrown
            if([statusCodes.instruction_update_rejected, statusCodes.subscription_update_rejected].includes(response.status)) {
                let json = await response.json();
                let error = new Error(json.message);
                error.code = response.status;
                error.data = json;
                throw error;
            }

            // prevent moving forward if an error was thrown
            if(response.status !== 200) {
                let text = await response.text();
                let error = new Error(text || 'An unknown error occurred');
                error.code = response.status;
                throw error;
            }

            let json = await response.json();
            resolve(json);

        } catch(e) {
            reject(e);
        }
    });
}

const post = async (utils, endpoint, props, options = {}) => {
    return new Promise(async (resolve, reject) => {
        try {

            // determine if a post with download was requested
            if(options?.response_type === 'file_download') {
                
                // build url and xml request
                let xhr = new XMLHttpRequest();
                xhr.open('POST', `${API.server}/api/v${API.version}${endpoint}`);
                xhr.withCredentials = true;

                // set authentication headers for request
                let headers = utils.api.headers();
                Object.keys(headers).filter(key => key !== 'Content-Type').forEach(key => xhr.setRequestHeader(key, headers[key]));

                // send request to server declare event handlers for response
                xhr.send(JSON.stringify(props));
                xhr.onreadystatechange = () => {
                    if(xhr.readyState == 2) {
                        xhr.responseType = xhr.status == 200 ? 'blob' :'text';
                    }
                    if(xhr.readyState == 4) {

                        // throw an upstream error if session has expired
                        if(xhr.status === statusCodes.expired_session) {
                            return showSessionExpiredAlert(utils);
                        }
                        
                        // prevent moving forward if any other status than success is returned
                        if(xhr.status !== 200) {
                            let error = new Error(xhr.responseText || 'An unknown error occurred');
                            reject(error);
                            return;
                        }
                        
                        // check for conflicts and allow user to respond
                        if(xhr.status === 409) {
                            let error = new Error();
                            error.code = xhr.status;
                            error.category = xhr.response.category;
                            error.message = xhr.response.message;
                            error.response = xhr.response;
                            reject(error);
                            return;
                        }

                        // prepare link element for automatic download
                        let fileName = xhr.getResponseHeader('Content-Disposition')?.split('attachment;filename=')[1];
                        resolve({ 
                            file_name: fileName,
                            url: window.URL.createObjectURL(xhr.response) 
                        });
                    }
                }
                return;
            }

            // prepare and submit request to sever
            let response = await fetch(`/api/v${API.version}${endpoint}`, {
                method: 'POST',
                withCredentials: true,
                headers: utils.api.headers(),
                body: JSON.stringify(props)
            });

            // determine if response code represents a 5xx level error
            if([502, 503, 504].includes(response.status)) {
                throw new Error('Please try again in a few minutes');
            }

            // throw an upstream error if session has expired
            if(response.status === statusCodes.expired_session) {
                return showSessionExpiredAlert(utils);
            }

            // determine if an error with a payload was thrown
            if([statusCodes.instruction_update_rejected, statusCodes.subscription_update_rejected].includes(response.status)) {
                let json = await response.json();
                let error = new Error(json.message);
                error.code = response.status;
                error.data = json;
                throw error;
            }

            // prevent moving forward if an error was thrown
            if(response.status !== 200) {
                let text = await response.text();
                let error = new Error(text || 'An unknown error occurred');
                error.code = response.status;
                throw error;
            }

            let json = await response.json();
            resolve(json);

        } catch(e) {
            reject(e);
        }
    });
}

const showSessionExpiredAlert = utils => {
    utils.alert.show({
        title: 'Session Expired',
        message: `Your session has expired. Please log in again to continue using LifeWipe's features and services securely.`,
        buttons: [{
            key: 'confirm',
            title: 'Okay',
            style: 'default'
        }],
        onClick: () => {
            utils.user.logout();
            setTimeout(() => {
                utils.user.request.login();
            }, 500);
        }
    });
}

const upload = async (utils, url, props, options = {}) => {
    return new Promise(async (resolve, reject) => {
        try {

            // declare request url
            let request = new URL(`${API.server}/api/v${API.version}${url}`);

            // build url and xml request
            let xhr = new XMLHttpRequest();
            xhr.open('POST', request);

            // set authentication headers for request
            let headers = utils.api.headers();
            Object.keys(headers).filter(key => key !== 'Content-Type').forEach(key => xhr.setRequestHeader(key, headers[key]));

            // prepare form components for request
            let data = new FormData();
            Object.keys(props).forEach(key => {
                if(typeof(props[key]) !== 'object') {
                    return data.append(key, props[key]);
                }
                return data.append(key, JSON.stringify(props[key]));
            });

            // add file data to form data object
            if(options.files) {
                options.files.forEach(file => data.append(file.key, file.data));
            }

            // set up event handlers before sending
            xhr.onerror = () => {
                let error = new Error(xhr.responseText || 'Upload failed');
                reject(error);
            }
            
            xhr.onabort = () => {
                let error = new Error('Upload cancelled');
                error.name = 'AbortError';
                reject(error);
            }
            
            // set up upload progress handler before sending
            if(xhr.upload && typeof(options.onProgressChange) === 'function') {
                xhr.upload.onprogress = evt => {
                    options.onProgressChange({
                        current: evt.loaded,
                        progress: Math.round((evt.loaded / evt.total) * 100),
                        total: evt.total || 0
                    });
                }
            }
            
            xhr.onload = () => {

                // throw an upstream error if session has expired
                if(xhr.status === statusCodes.expired_session) {
                    return showSessionExpiredAlert(utils);
                }
                
                // prevent moving forward if any other status than success is returned
                if(xhr.status !== 200) {
                    let error = new Error(xhr.responseText || 'An unknown error occurred');
                    reject(error);
                    return;
                }

                // check for conflicts and allow user to respond
                if(xhr.status === 409) {
                    let error = new Error();
                    error.code = xhr.status;
                    error.category = xhr.response.category;
                    error.message = xhr.response.message;
                    error.response = xhr.response;
                    reject(error);
                    return;
                }

                // return json response
                let json = JSON.parse(xhr.response);
                resolve(json);
            }

            // add abort controller signal if provided
            if(options.signal) {
                options.signal.addEventListener('abort', () => {
                    xhr.abort();
                });
            }

            // send request to server
            xhr.send(data);

        } catch(e) {
            reject(e);
        }
    })
}

const statusCodes = {
    expired_session: 419,
    invalid_trusted_device_token: 437,
    subscription_upgrade_required: 420,
    instruction_update_rejected: 430,
    subscription_update_rejected: 432,
    mfa_required: 433,
    failed_login: 434,
    rejected_partnership_request: 435,
    incomplete_user_profile: 436,
    subscription_required: 438,
    draft_mode_failure: 439,
    confirmation_required: 440
}

const Request = {
    status: {
        get: () => statusCodes
    },
    download: download,
    get: get,
    post: post,
    upload: upload
}
export default Request;
